/**
 * Guest transfer page — wire types, fetch, and the PHP page's formatting
 * rules. Mirrors api/Modules/Coportal/Services/PublicTransferResolver.cs:
 * the view carries only what the PHP page prints (index.php:736-947).
 */

import { COMPANY } from '@/shared/company';
import type { ResolvedLayout } from './transferLayout';

export type PublicBrandType = 'default' | 'kuoni' | 'whitelabel';

export interface PublicTransferBrand {
  name: string;
  type: PublicBrandType;
  logoUrl: string | null;
  /** Kuoni only — the header's second (Asian Trails) logo. */
  secondLogoUrl: string | null;
  accent: string;
  accentDark: string;
  accentBg: string;
  accentBorder: string;
  accentBorderLight: string;
  contactBg: string;
  thColor: string;
  /** composeBrandSig(st_brand_sig, name), narrowed to the region. */
  signature: string;
  /**
   * The agent's destinations (agents.st_region): 'my' or 'sg' shows that
   * country's contacts, guides and excursions alone; null shows both.
   */
  region?: 'my' | 'sg' | null;
}

export interface PublicTransferService {
  /** yyyy-MM-dd */
  date: string | null;
  pax: number | null;
  /** HH:MM; null when the log has no time (or 00:00). */
  time: string | null;
  pickup: string | null;
  dropoff: string | null;
  flight: string | null;
  product: string | null;
}

export interface PublicWelcomeMeeting {
  date: string | null;
  time: string | null;
  rep: string;
  location: string | null;
  photoUrl: string | null;
  phone: string | null;
  /** Digits for wa.me. */
  whatsapp: string | null;
}

export interface PublicAttachment {
  name: string | null;
  url: string | null;
  type: string | null;
  size: number;
}

export interface PublicTransferView {
  /** 'none' = unknown ref OR nothing upcoming — the PHP page does not tell them apart. */
  state: 'found' | 'none';
  companyName: string;
  companyLogoUrl: string | null;
  ref: string | null;
  brand: PublicTransferBrand | null;
  period: { from: string; to: string } | null;
  services: PublicTransferService[] | null;
  welcomeMeetings: PublicWelcomeMeeting[] | null;
  attachments: PublicAttachment[] | null;
  /**
   * The Guest Portal's switches (hub_settings.guest_portal, brief §8.1).
   * ABSENT while the tenant never saved any (Asian Trails): the page is then
   * exactly what it always was.
   */
  portal?: PublicGuestPortal | null;
  /**
   * The page's blocks (Guest Portal ▸ Transfer page, 2026-10-29), resolved
   * for this booking with each link row's pills filled. ABSENT from an older
   * API: the page then derives the layout from `portal` (transferLayout.ts).
   */
  layout?: ResolvedLayout | null;
}

/** The guest page's parts the Guest Portal switches (api GuestPortalSettings.Sections). */
export type GuestSectionId =
  | 'header'
  | 'services'
  | 'meetingPoint'
  | 'explore'
  | 'recommendations'
  | 'driverLink'
  | 'feedbackLink'
  | 'customPages'
  | 'files'
  | 'contact'
  | 'privacy';

/** The body's blocks, in the order the page has always drawn them. */
export const DEFAULT_BLOCK_ORDER = ['services', 'meetingPoint', 'explore', 'files', 'contact'] as const;
export type GuestBlockId = (typeof DEFAULT_BLOCK_ORDER)[number];

export interface GuestLookOverrides {
  logoUrl?: string;
  accent?: string;
  signOff?: string;
  hotlineMy?: string;
  hotlineSg?: string;
  csEmailMy?: string;
  csEmailSg?: string;
}

/** One Explore pill; `country` 'my' / 'sg' follows an agent's destinations, null is always shown. */
export interface RecoPill {
  slug: string;
  name: string;
  country: string | null;
}

export interface PublicGuestPortal {
  sections: Partial<Record<GuestSectionId, boolean>>;
  order: string[];
  look: GuestLookOverrides;
  privacyNotice: string | null;
  /** The tenant's own destinations with Local Recommendations; null = the built-in list. */
  places: RecoPill[] | null;
  /** The tenant's own destinations with optional tours; null = the built-in list. */
  tours: RecoPill[] | null;
  /**
   * Pills to the tenant's listed guest pages (Guest Portal ▸ Pages), only
   * while Sections ▸ "Your guest pages" is on. ABSENT otherwise.
   */
  pages?: GuestPagePill[] | null;
}

/** A pill to a guest page (/p/<slug>); `countries` 'my' / 'sg' follow an agent's destinations, null is everyone. */
export interface GuestPagePill {
  slug: string;
  title: string;
  countries: string[] | null;
}

/** The guest pages this booking's agent works in: every page for none, or those of its country. */
export function pagesFor(region: string | null | undefined, pages: readonly GuestPagePill[] | null | undefined): GuestPagePill[] {
  return (pages ?? []).filter((p) => !p.countries?.length || !region || p.countries.includes(region));
}

export class PublicFetchError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'PublicFetchError';
  }
}

export async function fetchPublicTransfer(
  ref: string,
  signal?: AbortSignal,
  /** The sample booking's agent, by id (`?a=`): brands the sample page. Real refs ignore it. */
  sampleAgentId?: string | null,
): Promise<PublicTransferView> {
  const qs = sampleAgentId ? `?a=${encodeURIComponent(sampleAgentId)}` : '';
  const res = await fetch(`/api/public/transfers/${encodeURIComponent(ref)}${qs}`, {
    method: 'GET',
    // Anonymous page: never send a staff session along with a guest lookup.
    credentials: 'omit',
    signal,
  });
  if (!res.ok) {
    let msg = `Request failed with status ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* non-JSON 5xx */
    }
    throw new PublicFetchError(res.status, msg);
  }
  const body = (await res.json()) as { ok?: boolean; data?: PublicTransferView };
  if (!body?.data) throw new PublicFetchError(500, 'Empty response');
  return body.data;
}

// ─── PHP content rules ───────────────────────────────────────────────

/** index.php:580 / :450 / :411 — also what the server injects for link previews. */
export function foundTitle(brandName: string): string {
  return `Your Transfers & Excursions — ${brandName}`;
}
export function noneTitle(companyName: string): string {
  return `Your upcoming transfers & excursions — ${companyName}`;
}
/**
 * PHP portal-settings.php:761: the running company's name, used only when the
 * server could not answer (read at call time, so a saved profile wins).
 */
export function errorTitle(title: string, companyName = COMPANY.fullName): string {
  return `${title} — ${companyName}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isoParts(iso: string | null | undefined): [string, string, string] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? [m[1] ?? '', m[2] ?? '', m[3] ?? ''] : null;
}

/** PHP date('d/m/Y') — the table, cards and welcome meetings. */
export function formatDmy(iso: string | null | undefined): string {
  const p = isoParts(iso);
  return p ? `${p[2]}/${p[1]}/${p[0]}` : '-';
}

/** PHP date('d M Y') — the schedule note ("28 Sep 2026"). Locale-free on purpose. */
export function formatDayMonYear(iso: string | null | undefined): string {
  const p = isoParts(iso);
  if (!p) return '';
  return `${p[2]} ${MONTHS[Number(p[1]) - 1] ?? ''} ${p[0]}`;
}

/** index.php:913 — `< 1 MB → round(KB) . 'KB'`, else one decimal MB. */
export function fileSizeLabel(bytes: number): string {
  return bytes < 1048576
    ? `${Math.round(bytes / 1024)}KB`
    : `${(bytes / 1048576).toFixed(1)}MB`;
}

/** index.php:67-75 — the static destination list behind every pill row. */
export const RECOS: ReadonlyArray<{ slug: string; name: string; country: 'my' | 'sg' }> = [
  { slug: 'kuala-lumpur', name: 'Kuala Lumpur', country: 'my' },
  { slug: 'singapore', name: 'Singapore', country: 'sg' },
  { slug: 'penang', name: 'Penang', country: 'my' },
  { slug: 'langkawi', name: 'Langkawi', country: 'my' },
  { slug: 'kota-kinabalu', name: 'Kota Kinabalu', country: 'my' },
  { slug: 'kuching', name: 'Kuching', country: 'my' },
  { slug: 'malacca', name: 'Malacca', country: 'my' },
];

/** The look the page wears: the Guest Portal's overrides over the company profile (build args). */
export interface GuestLook {
  logoUrl: string;
  accent: string;
  signOff: string;
  hotlineMy: string;
  hotlineSg: string;
  csEmailMy: string;
  csEmailSg: string;
}

export function guestLook(portal: PublicGuestPortal | null | undefined): GuestLook {
  const o = portal?.look ?? {};
  return {
    logoUrl: o.logoUrl || COMPANY.logoUrl,
    accent: o.accent || COMPANY.accent,
    signOff: o.signOff || COMPANY.signOff,
    hotlineMy: o.hotlineMy || COMPANY.hotlineMy,
    hotlineSg: o.hotlineSg || COMPANY.hotlineSg,
    csEmailMy: o.csEmailMy || COMPANY.csEmailMy,
    csEmailSg: o.csEmailSg || COMPANY.csEmailSg,
  };
}

/** Is this part of the page on? Everything is, unless the Guest Portal switched it off. */
export function sectionOn(portal: PublicGuestPortal | null | undefined, id: GuestSectionId): boolean {
  return portal?.sections?.[id] !== false;
}

/** The body's blocks in the Guest Portal's order (unknown ids dropped, missing ones after, in today's order). */
export function blockOrder(portal: PublicGuestPortal | null | undefined): GuestBlockId[] {
  const known = new Set<string>(DEFAULT_BLOCK_ORDER);
  const listed: GuestBlockId[] = [];
  for (const id of portal?.order ?? []) {
    if (known.has(id) && !listed.includes(id as GuestBlockId)) listed.push(id as GuestBlockId);
  }
  return [...listed, ...DEFAULT_BLOCK_ORDER.filter((id) => !listed.includes(id))];
}

/** The countries a page shows: the agent's one, or both (null / unknown). */
export function shownCountries(region: string | null | undefined): { my: boolean; sg: boolean } {
  return { my: region !== 'sg', sg: region !== 'my' };
}

/**
 * The destination pills for the agent's countries: the built-in list, or the
 * tenant's own (Guest Portal ▸ Sections, "from Local Recommendations"). A
 * pill with no country of the two is always shown.
 */
export function recosFor(region: string | null | undefined, pills: readonly RecoPill[] | null = null): RecoPill[] {
  const shown = shownCountries(region);
  return (pills ?? RECOS).filter((rc) => rc.country !== 'my' && rc.country !== 'sg' ? true : shown[rc.country]);
}

/**
 * The guest page that stays on the PHP host: /feedback (index.php:881). The
 * company profile's guest base (`VITE_GUEST_BASE_URL`), read at call time:
 * on a stack serving many companies it is the page's own origin.
 *
 * Amassa does NOT serve it (nginx/default.conf sends only /<ref>, /cs/<ref>,
 * /transfers/<ref> and guide|places|tours|gifts|gift-list|pickup|p to the guest
 * pages; PublicPagesGate claims the same). On a host with no PHP pages behind
 * it, such as the demo's, it falls through to the staff app: a dead link.
 * Guest Portal ▸ Sections switches it off ("feedbackLink"). /pickup was the
 * other one until 2026-10-26; Amassa serves it now.
 */
export function phpPublic(): string {
  return COMPANY.guestBaseUrl;
}
